import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { PROJECTS, COMMON_PROJECT_BENEFITS } from "../../lib/data";
import { COMPANY, buildWhatsAppLink, telLink } from "../../lib/site";
import {
  ArrowRight,
  Check,
  MapPin,
  Tag,
  Sparkles,
  Phone,
  MessageCircle,
  BadgeCheck,
} from "lucide-react";

export default function FeaturedProjects({ onOpenLead }) {
  return (
    <section
      data-testid="featured-projects"
      className="relative py-28 md:py-40 px-6 md:px-10 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto">
        {/* Heading */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <div className="eyebrow mb-4">Featured Projects</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white font-light leading-[1.05]">
            Premium Investment{" "}
            <span className="gold-gradient-text italic">Opportunities</span> in
            Dholera Smart City.
          </h2>
          <p className="mt-8 text-white/60 text-lg font-light leading-relaxed">
            Explore carefully selected residential and investment projects
            designed for future growth, smart living, and long-term wealth
            creation.
          </p>
        </div>

        {/* Project cards */}
        <div className="space-y-8 md:space-y-10">
          {PROJECTS.map((p, i) => (
            <motion.div
              key={p.id}
              data-testid={`project-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.05 }}
              className={`grid lg:grid-cols-12 gap-0 glass overflow-hidden group hover:border-[#D4AF37]/30 transition-all duration-500 ${
                i % 2 === 1 ? "lg:[direction:rtl]" : ""
              }`}
            >
              <Link
                to={`/projects/${p.slug}`}
                className="lg:col-span-6 relative aspect-[16/10] lg:aspect-auto overflow-hidden [direction:ltr] block"
                data-testid={`project-${i}-image-link`}
              >
                <img
                  src={p.image}
                  alt={p.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms] ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />
                <span className="absolute top-5 left-5 bg-black/60 backdrop-blur-md border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] tracking-[0.25em] uppercase px-3 py-1.5">
                  Project {String(i + 1).padStart(2, "0")}
                </span>
              </Link>

              <div className="lg:col-span-6 p-8 md:p-12 lg:p-14 flex flex-col justify-center [direction:ltr]">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">
                    {p.category}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-white/30" />
                  <span className="flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-white/50">
                    <MapPin
                      className="w-3 h-3 text-[#D4AF37]"
                      strokeWidth={1.6}
                    />
                    {p.location}
                  </span>
                </div>

                <h3 className="font-serif text-3xl md:text-4xl text-white font-light leading-tight">
                  {p.title}
                </h3>

                {/* Highlight strip */}
                <div className="mt-5 inline-flex items-start gap-3 px-4 py-3 border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06] self-start max-w-full">
                  <Sparkles
                    className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0"
                    strokeWidth={1.4}
                  />
                  <span className="text-[#F5E6CE] text-sm font-light tracking-wide">
                    {p.highlight}
                  </span>
                </div>

                {/* Specs row */}
                <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-start gap-2">
                    <Tag
                      className="w-3.5 h-3.5 text-[#D4AF37] mt-0.5"
                      strokeWidth={1.6}
                    />
                    <div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-white/40">
                        Property Type
                      </div>
                      <div className="text-white/85 font-light">
                        {p.propertyType}
                      </div>
                    </div>
                  </div>
                  {p.startingPrice && (
                    <div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-white/40">
                        Starting From
                      </div>
                      <div className="font-serif text-xl text-[#D4AF37]">
                        {p.startingPrice}
                      </div>
                    </div>
                  )}
                  {p.plotSize && (
                    <div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-white/40">
                        Plot Size
                      </div>
                      <div className="text-white/85 font-light">
                        {p.plotSize}
                      </div>
                    </div>
                  )}
                  {p.totalUnits && (
                    <div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-white/40">
                        Total Units
                      </div>
                      <div className="text-white/85 font-light">
                        {p.totalUnits}
                      </div>
                    </div>
                  )}
                </div>

                <p className="mt-6 text-white/60 leading-relaxed font-light">
                  {p.overview}
                </p>

                <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2">
                  {p.benefits.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-2 text-sm text-white/75 font-light"
                    >
                      <Check
                        className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0"
                        strokeWidth={1.6}
                      />
                      {b}
                    </li>
                  ))}
                </ul>

                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <button
                    data-testid={`project-${i}-cta`}
                    onClick={onOpenLead}
                    className="inline-flex items-center gap-3 bg-[#D4AF37] text-black font-medium tracking-[0.2em] uppercase text-xs px-7 py-3.5 hover:bg-[#B3932E] transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.25)] hover:shadow-[0_0_35px_rgba(212,175,55,0.5)]"
                  >
                    {p.cta}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <Link
                    to={`/projects/${p.slug}`}
                    data-testid={`project-${i}-view-details`}
                    className="inline-flex items-center gap-2 text-[#D4AF37] text-xs tracking-[0.25em] uppercase border-b border-[#D4AF37]/50 pb-1 hover:border-[#D4AF37] hover:gap-3 transition-all duration-300"
                  >
                    View Details & Gallery
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Common project benefits */}
        <div className="mt-24 glass-strong p-8 md:p-12">
          <div className="text-center mb-10">
            <div className="eyebrow mb-3">Why every Mayur project</div>
            <h3 className="font-serif text-3xl md:text-4xl text-white font-light">
              Built on the{" "}
              <span className="gold-gradient-text italic">same promise</span>.
            </h3>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-5">
            {COMMON_PROJECT_BENEFITS.map((b) => (
              <div key={b} className="flex items-start gap-3">
                <BadgeCheck
                  className="w-5 h-5 text-[#D4AF37] mt-0.5 shrink-0"
                  strokeWidth={1.4}
                />
                <span className="text-white/80 font-light">{b}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom section CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-20 relative overflow-hidden glass-strong p-10 md:p-16 text-center"
        >
          <div
            className="absolute inset-0 -z-10 opacity-40"
            style={{
              backgroundImage:
                "radial-gradient(circle at 30% 50%, rgba(212,175,55,0.18), transparent 50%), radial-gradient(circle at 80% 50%, rgba(212,175,55,0.1), transparent 50%)",
            }}
          />
          <h3 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white font-light leading-[1.05]">
            Ready to invest in{" "}
            <span className="gold-gradient-text italic">Dholera Smart City?</span>
          </h3>
          <p className="mt-6 text-white/65 text-lg font-light max-w-2xl mx-auto">
            Book a free consultation and site visit with our property experts
            today.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <button
              data-testid="projects-cta-schedule"
              onClick={onOpenLead}
              className="inline-flex items-center gap-3 bg-[#D4AF37] text-black font-medium tracking-[0.2em] uppercase text-xs px-8 py-4 hover:bg-[#B3932E] transition-all duration-300 shadow-[0_0_30px_rgba(212,175,55,0.3)] hover:shadow-[0_0_45px_rgba(212,175,55,0.55)]"
            >
              Schedule Site Visit
              <ArrowRight className="w-4 h-4" />
            </button>
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="projects-cta-whatsapp"
              className="inline-flex items-center gap-2 px-6 py-4 border border-[#25D366]/60 text-[#25D366] text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#25D366]/10 transition-all duration-300"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.6} />
              WhatsApp Now
            </a>
            <a
              href={telLink(COMPANY.phones[1])}
              data-testid="projects-cta-call"
              className="inline-flex items-center gap-2 px-6 py-4 border border-white/20 text-white text-xs tracking-[0.2em] uppercase font-medium hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
            >
              <Phone className="w-4 h-4" strokeWidth={1.6} />
              Call Now
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
